import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";

import {ImagePreview} from "../image-attach/image-preview.tsx";
import {FullscreenImageModal} from "../messages/fullscreen-image-modal.tsx";

import xCircleIcon from "@phosphor-icons/core/fill/x-circle-fill.svg";

export interface ImageProps {
    image: string;
    altText?: string;
    onDelete?: () => void;
}

export const ChatBoxImageAttachment = React.memo(
    ({image, altText, onDelete}: ImageProps): React.ReactElement => (
        <View style={styles.container}>
            <ModalLauncher
                aria-label={t`View full size image`}
                modal={<FullscreenImageModal image={image} altText={altText} />}
            >
                {({openModal}) => (
                    <Clickable
                        testId="chat-input-image-thumbnail"
                        style={styles.clickableContainer}
                        onClick={openModal}
                    >
                        {() => (
                            <ImagePreview
                                displayWidth={64}
                                displayHeight={64}
                                image={image}
                                altText={altText}
                            />
                        )}
                    </Clickable>
                )}
            </ModalLauncher>
            <IconButton
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.();
                }}
                disabled={false}
                icon={xCircleIcon}
                kind="tertiary"
                actionType="neutral"
                size="small"
                style={styles.detachButton}
                aria-label={t`Delete image`}
            />
        </View>
    ),
);

const styles = StyleSheet.create({
    container: {
        margin: spacing.xSmall_8,
        marginBlockEnd: 0,
        width: 80,
    },
    clickableContainer: {
        cursor: "pointer",
        // Add subtle hover effect
        ":hover": {
            opacity: 0.8,
        },
        // Add focus ring for accessibility
        ":focus": {
            outline: `2px solid ${semanticColor.focus.outer}`,
            outlineOffset: "2px",
        },
    },
    detachButton: {
        position: "absolute",
        top: -4,
        insetInlineEnd: 8,
        // The styling below ensures the "X" is opaque, and doesn't let the
        // image color bleed through.
        backgroundColor: semanticColor.core.background.base.subtle,
        width: spacing.medium_16,
        height: spacing.medium_16,
        borderRadius: "50%",
    },
});
